import type {
  NotificationNode,
  EditableGraph,
  Id,
  Graph,
} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'draft-js/lib/RawDraftContentState'. '/Users/diwaker/SpacedOut/ui-server/node_modules/draft-js/lib/RawDraftContentState.js' implicitly has an 'any' type.
import type {RawDraftContentState} from 'draft-js/lib/RawDraftContentState';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch} from 'react-redux';
import {convertFromRaw} from 'draft-js';

import {useAtsVariables, useDraftDecorator, useEditorState} from './reducer';
import {keyBy} from 'src/utils/map';
import classify from 'src/utils/classify';
import {hasText, testRegex} from 'src/utils/draft';
import {validateNode} from './validation';
import {updateFlowConfig} from 'src/components/conversation-builder/thunks';
import {singleDynamicRegex} from 'src/components/lib/markdown-editor/dynamic-text';
import VariablePickerSelect from 'src/components/lib/variable-picker/select-2';
import {findAncestorNodeVariables} from '../utils';
import EditorContainer from './container';
import {
  UncontrolledDraftEditor,
  prepareForServer,
  nodeVariableDecorator,
  MessageDraftEditor,
} from 'src/components/conversation-builder/draft-editor';
import {FormPair, ButtonsSmall} from 'src/components/conversation-builder/form';
import Popover from 'src/components/conversation-builder/popover';
// @ts-expect-error - TS7016 - Could not find a declaration file for module '@spaced-out/ui-lib/lib/button'. '/Users/diwaker/SpacedOut/ui-server/node_modules/@spaced-out/ui-lib/lib/button/index.js' implicitly has an 'any' type.
import {UnstyledButton} from '@spaced-out/ui-lib/lib/button';
import Button from 'src/components/lib/new-button';

import {PadSection, useFirstSmsMessage} from './common';
import Toggle from 'src/components/lib/toggle/toggle';

import css from 'src/components/conversation-builder/popover.css';


export default function FeedbackEditor({
  graph,
  editableGraph,
  entityType,
  edgeId,
  readOnly,
  onClose,
  setFlow,
  ...props
}: {
  graph: Graph;
  editableGraph: EditableGraph;
  entityType: EntityType;
  onClose: () => unknown;
  edgeId?: Id;
  readOnly: boolean;
  setFlow?: (arg1: Graph) => void;
}) {
  const dispatch = useDispatch();
  const {t, makeKey} = useLabelTranslation();
  const [config, setConfig] = React.useState(graph.flow_config ?? {});

  const handleSubmit = () => {
    dispatch(updateFlowConfig(graph.id, config)).then((updatedFlowConfig) => {
      if (setFlow) {
        //@ts-expect-error
        setFlow({...graph, flow_config: updatedFlowConfig});
      }
      onClose();
    });
  };
  return (
    <Popover
      titleText={t('Edit_Feedback_Node', 'Edit Feedback Node')}
      titleContent={
        <h1 className={css.titleLarge}>
          {t('Feedback_Node', 'Feedback Node')}
        </h1>
      }
      onClose={onClose}
    >
      <form
        className={css.feedbackContainer}
        onSubmit={(event) => {
          event.preventDefault();
          if (!readOnly) {
            handleSubmit();
          }
        }}
      >
        <PadSection>
          <div>
            {!readOnly && (
              <Toggle
                className={css.toggle}
                checked={config.feedback_enabled}
                // checked={true}
                onChange={(e) => {
                  if (config.feedback_enabled) {
                    setConfig({...config, feedback_enabled: false});
                  } else {
                    setConfig({
                      ...config,
                      feedback_enabled: true,
                    });
                  }
                }}
              />
            )}
            <div className={css.formPair}>
              <strong>
                {config.feedback_enabled
                  ? t('Enabled', 'Enabled')
                  : t('Disabled', 'Disabled')}
              </strong>
              {readOnly
                ? config.feedback_enabled
                  ? t(
                      'Feedback_will_be_collected_at_the_end_of_every_conversation_with_this_chatbot',
                      'Feedback will be collected at the end of every conversation with this chatbot',
                    )
                  : t(
                      'Feedback_will_not_be_collected_after_conversation_with_this_chatbot',
                      'Feedback will not be collected after conversation with this chatbot',
                    )
                : `${t(
                    'This_node_will_be_added_to_the_end_of_the_conversation',
                    'This node will be added to the end of the conversation',
                  )}.`}
            </div>
          </div>
        </PadSection>

        <PadSection>
          {readOnly ? (
            <div className={css.formPair}>
              <strong>{`${t(
                'Feedback_Question',
                'Feedback Question',
              )}:`}</strong>
              {t(
                makeKey(
                  convertFromRaw(config.feedback_question).getPlainText(),
                ),
                convertFromRaw(config.feedback_question).getPlainText(),
              )}
            </div>
          ) : (
            <MessageDraftEditor
              label={`${t('Feedback_Question', 'Feedback Question')}*`}
              placeholder=""
              defaultValue={config.feedback_question}
              onChange={(feedback_question) =>
                setConfig({...config, feedback_question})
              }
              entityType={entityType}
              isFirstSmsMessage={false}
              // error={error?.text_draft_json}
              readOnly={!config.feedback_enabled}
              useCase={graph.use_case}
            />
          )}
        </PadSection>

        <PadSection>
          <div className={css.formPair}>
            <strong>{`${t(
              'Rating_Scale_Preview',
              'Rating Scale Preview',
            )}:`}</strong>
            <FeedbackPreview disabled={!config.feedback_enabled} />
          </div>
        </PadSection>

        <PadSection>
          <ButtonsSmall>
            <Button size="medium" onClick={onClose}>
              {readOnly ? t('Done', 'Done') : t('Cancel', 'Cancel')}
            </Button>
            {!readOnly && (
              <Button
                type="primary"
                size="medium"
                onClick={handleSubmit}
                disabled={!hasText(config.feedback_question)}
              >
                {t('Save', 'Save')}
              </Button>
            )}
          </ButtonsSmall>
        </PadSection>
      </form>
    </Popover>
  );
}

function FeedbackPreview({disabled}: {disabled: boolean}) {
  const {t} = useLabelTranslation();
  const feedbackValues = [
    {value: '1', label: t('Very_Bad', 'Very Bad')},
    {value: '2', label: t('Bad', 'Bad')},
    {value: '3', label: t('Ok', 'Ok')},
    {value: '4', label: t('Good', 'Good')},
    {value: '5', label: t('Very_Good', 'Very Good')},
  ];
  return (
    <div>
      {feedbackValues.map(({value, label}, index) => (
        <UnstyledButton
          className={classify(css.button, {[css.disabledButtons]: disabled})}
          key={value}
          onClick={() => {}}
          style={{
            backgroundColor: css['color' + index],
          }}
        >
          <span className={css.feedbackNumber}>{index + 1}</span>

          <span className={css.feedbackLabel}>{label}</span>
        </UnstyledButton>
      ))}
    </div>
  );
}
